import { useEffect, useState } from 'react';
import { Loader2, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';

interface CounselorRow {
  counselorId: string;
  counselorName: string;
  totalMeetings: number;
  avgDelayMin: number;
  lateCount: number;
  onTimeCount: number;
  latePercent: number;
  maxDelayMin: number;
}

interface Report {
  lateThresholdMin: number;
  counselors: CounselorRow[];
}

export function MeetingPunctualityReport({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }) {
  const [data, setData] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get('/availability/reports/punctuality', { params: { dateFrom, dateTo } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const rows = data?.counselors ?? [];
  const threshold = data?.lateThresholdMin ?? 5;

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 px-6 py-14 text-center">
        <Clock className="h-10 w-10 mx-auto text-slate-300 mb-3" />
        <p className="text-sm font-semibold text-slate-700">No attended meetings yet</p>
        <p className="text-xs text-slate-500 mt-1">
          Punctuality is measured from the host's Zoom join time vs the scheduled start.
          Data appears once meetings run with Zoom webhooks connected.
        </p>
      </div>
    );
  }

  // Totals across all counselors.
  const totalMeetings = rows.reduce((s, r) => s + r.totalMeetings, 0);
  const totalLate = rows.reduce((s, r) => s + r.lateCount, 0);
  const overallLatePct = totalMeetings ? Math.round((totalLate / totalMeetings) * 100) : 0;

  const delayCls = (m: number) => (m > threshold ? 'text-red-600' : m > 0 ? 'text-amber-600' : 'text-emerald-600');

  return (
    <div className="space-y-4">
      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500">Meetings measured</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{totalMeetings}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500">Late meetings (&gt;{threshold}m)</p>
          <p className="mt-1 text-2xl font-bold text-red-600">{totalLate}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500">Overall late rate</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{overallLatePct}%</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-900">Counselor Punctuality</h3>
          <p className="text-xs text-slate-500">Host join time vs scheduled start. "Late" = joined &gt; {threshold} min after start.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                <th className="px-4 py-2 font-semibold">Counselor</th>
                <th className="px-4 py-2 font-semibold text-right">Meetings</th>
                <th className="px-4 py-2 font-semibold text-right">Avg delay</th>
                <th className="px-4 py-2 font-semibold text-right">On time</th>
                <th className="px-4 py-2 font-semibold text-right">Late</th>
                <th className="px-4 py-2 font-semibold text-right">Late %</th>
                <th className="px-4 py-2 font-semibold text-right">Max delay</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.counselorId} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.counselorName}</td>
                  <td className="px-4 py-2.5 text-right text-slate-600">{r.totalMeetings}</td>
                  <td className={`px-4 py-2.5 text-right font-semibold ${delayCls(r.avgDelayMin)}`}>{r.avgDelayMin}m</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" />{r.onTimeCount}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="inline-flex items-center gap-1 text-red-600"><AlertTriangle className="h-3.5 w-3.5" />{r.lateCount}</span>
                  </td>
                  <td className={`px-4 py-2.5 text-right font-semibold ${r.latePercent > 30 ? 'text-red-600' : 'text-slate-600'}`}>{r.latePercent}%</td>
                  <td className="px-4 py-2.5 text-right text-slate-600">{r.maxDelayMin}m</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
